import { useState, useEffect } from 'react';
import { X, Shield, ShieldCheck, ShieldOff, Globe, Mail } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { updateUser, deactivateUser, reactivateUser, setUserSiteAccess, setUserGroups, sendResetEmail, fetchUser, type AdminUser } from '../../services/adminApi';
import { getMfaStatus } from '../../services/mfaApi';
import { SiteAccessChecklist } from './SiteAccessChecklist';
import { UserGroupsChecklist } from './UserGroupsChecklist';
import { MfaSetupModal, MfaDisableModal } from './MfaSetupModal';
import { useAuthStore } from '../../store/authStore';

interface UserEditDrawerProps {
  user: AdminUser | null;
  onClose: () => void;
}

// Full role vocabulary, highest → lowest privilege. This MUST include every
// role a user can hold (matching UserTable + the admin API's frontend-facing
// role names). A `<select value={role}>` whose value matches no `<option>`
// silently falls back to the first option — which previously mis-rendered
// platform_admin / parent_org_admin users as "customer admin" and risked a
// silent downgrade on Save (#506). Backend `canAssignRole` still enforces that
// an admin cannot assign a role above their own.
const ASSIGNABLE_ROLES = [
  'platform_admin',
  'parent_org_admin',
  'customer_admin',
  'portfolio_manager',
  'site_operator',
  'analyst',
  'site_viewer',
];

export function UserEditDrawer({ user, onClose }: UserEditDrawerProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [role, setRole] = useState('');
  const [isPortalUser, setIsPortalUser] = useState(false);
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [messageKind, setMessageKind] = useState<'success' | 'error' | 'warning'>('success');
  // #361: when email transport is down, backend returns the reset URL so the
  // admin can deliver it out-of-band. Stored separately from the message so
  // we can render the URL in a code block with a copy button.
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [showMfaDisable, setShowMfaDisable] = useState(false);

  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);

  const showSuccess = (text: string) => {
    setMessageKind('success');
    setMessage(text);
    setResetLink(null);
  };
  const showError = (text: string) => {
    setMessageKind('error');
    setMessage(text);
    setResetLink(null);
  };
  const showResetLink = (text: string, url: string) => {
    setMessageKind('warning');
    setMessage(text);
    setResetLink(url);
  };

  // Check if editing self (MFA can only be managed for own account)
  const isEditingSelf = user?.user_id === currentUser?.user_id;

  // Fetch MFA status for the current user (only when editing self)
  const { data: mfaStatus, refetch: refetchMfaStatus } = useQuery({
    queryKey: ['mfa-status'],
    queryFn: getMfaStatus,
    enabled: isEditingSelf && !!user,
  });

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setCompanyName(user.company_name || '');
      setPhoneNumber(user.phone_number || '');
      setRole(user.role);
      setIsPortalUser(user.is_portal_user ?? false);
      setMessage('');
      setMessageKind('success');
      setResetLink(null);
      setLoadError(null);
      // Fetch full user details including site_access + group_memberships.
      // If the fetch fails, we deliberately do NOT reset siteIds/groupIds to []
      // — that would let a subsequent Save click silently wipe the edited user's
      // site access + group memberships. Instead we surface a red banner and
      // disable the Save button until the drawer is closed + reopened.
      fetchUser(user.user_id).then((fullUser) => {
        const access = fullUser.site_access || [];
        // Handle both string[] and {site_id: string}[] formats
        setSiteIds(access.map((s: any) => typeof s === 'string' ? s : s.site_id));
        const groups = fullUser.group_memberships ?? [];
        setGroupIds(groups.map((g) => g.org_unit_id));
      }).catch((err: unknown) => {
        const detail = err instanceof Error ? err.message : 'Unknown error';
        setLoadError(
          `Failed to load user details: ${detail}. Close and reopen this drawer to retry. ` +
          'Saving now would overwrite this user’s site access and group memberships with empty arrays.'
        );
      });
    }
  }, [user]);

  // Sequential 3-call save chain. If a later call fails, earlier calls have
  // already committed on the backend — the error banner surfaces the failing
  // step's error, and re-save is idempotent (updateUser unchanged fields no-op;
  // setUserSiteAccess and setUserGroups are diff-based on the backend). See
  // docs/plans/2026-04-22-org-hierarchy-phase3b-user-drawer-groups-design.md
  // "Partial-failure in the 3-call save chain" for the locked design decision.
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;
      await updateUser(user.user_id, {
        first_name: firstName,
        last_name: lastName,
        company_name: companyName,
        phone_number: phoneNumber,
        role,
        is_portal_user: isPortalUser,
      });
      await setUserSiteAccess(user.user_id, siteIds);
      await setUserGroups(user.user_id, groupIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['org-units'] });
      showSuccess('Saved');
    },
    onError: (err: Error) => {
      showError(err?.message || 'Failed to save');
    },
  });

  const handleSendResetEmail = async () => {
    if (!user) return;
    if (!confirm(`Send a password-reset email to ${user.email}?`)) return;
    try {
      const result = await sendResetEmail(user.user_id);
      if (result.email_dispatched) {
        showSuccess(`Reset email sent to ${user.email}.`);
      } else if (result.reset_url) {
        // #361: email transport is down but the backend gave us the reset URL
        // to deliver out-of-band. Render the copyable-link banner.
        showResetLink(result.message, result.reset_url);
      } else {
        // Defensive fallback for backends that don't yet ship #361 — preserves
        // pre-#361 behavior (red error banner with the operator-readable message).
        showError(result.message || 'Reset token generated, but email delivery is not configured.');
      }
    } catch (err: any) {
      const apiMsg = err?.response?.data?.error;
      showError(typeof apiMsg === 'string' ? apiMsg : 'Failed to send reset email.');
    }
  };

  const handleCopyResetLink = async () => {
    if (!resetLink) return;
    try {
      await navigator.clipboard.writeText(resetLink);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — leave the URL
      // visible in the banner so the admin can still select + copy manually.
    }
  };

  const handleDeactivate = async () => {
    if (!user || !confirm('Deactivate this user?')) return;
    try {
      await deactivateUser(user.user_id);
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    } catch {
      showError('Failed to deactivate');
    }
  };

  const handleReactivate = async () => {
    if (!user || !confirm('Reactivate this user?')) return;
    try {
      await reactivateUser(user.user_id);
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    } catch {
      showError('Failed to reactivate');
    }
  };

  const handleMfaComplete = () => {
    refetchMfaStatus();
    showSuccess(showMfaSetup ? 'MFA enabled successfully' : 'MFA disabled');
  };

  if (!user) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/20">
      <div className="w-full max-w-md bg-ems-bg-card h-full border-l border-ems-border overflow-y-auto animate-slide-in-right">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ems-border sticky top-0 bg-ems-bg-card z-10">
          <h2 className="text-lg font-semibold text-ems-text-primary">Edit User</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-ems-bg-card-hover text-ems-text-muted"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {!user.is_active && (
            <div className="p-3 rounded-lg bg-ems-amber/10 border border-ems-amber/20 text-ems-amber text-sm font-medium">
              This user is deactivated and cannot log in.
            </div>
          )}
          {message && (
            <div
              data-testid="user-edit-drawer-message"
              data-kind={messageKind}
              className={
                messageKind === 'error'
                  ? 'p-3 rounded-lg bg-ems-red/10 border border-ems-red/30 text-ems-red text-sm font-medium'
                  : messageKind === 'warning'
                  ? 'p-3 rounded-lg bg-ems-amber/10 border border-ems-amber/30 text-ems-amber text-sm font-medium'
                  : 'p-3 rounded-lg bg-ems-green/5 border border-ems-green/20 text-ems-green text-sm'
              }
            >
              {message}
            </div>
          )}
          {resetLink && (
            <div className="p-3 rounded-lg bg-ems-bg border border-ems-border space-y-2">
              <div className="text-xs uppercase tracking-wide text-ems-text-muted font-semibold">
                Reset Link
              </div>
              <code
                data-testid="user-edit-drawer-reset-link"
                className="block w-full px-2 py-1.5 rounded bg-ems-bg-card border border-ems-border text-xs text-ems-text-primary font-mono break-all select-all"
              >
                {resetLink}
              </code>
              <button
                data-testid="user-edit-drawer-copy-reset-link"
                onClick={handleCopyResetLink}
                className="w-full px-3 py-1.5 rounded-lg border border-ems-green/20 text-xs text-ems-green hover:bg-ems-green/5 font-medium"
              >
                Copy Link
              </button>
            </div>
          )}
          {loadError && (
            <div className="p-3 rounded-lg bg-ems-red/10 border border-ems-red/30 text-ems-red text-sm font-medium">
              {loadError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ems-text-primary mb-1">First Name</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green" />
            </div>
            <div>
              <label className="block text-sm font-medium text-ems-text-primary mb-1">Last Name</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ems-text-primary mb-1">Company Name</label>
              <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green" />
            </div>
            <div>
              <label className="block text-sm font-medium text-ems-text-primary mb-1">Phone Number</label>
              <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ems-text-primary mb-1">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green">
              {ASSIGNABLE_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-ems-text-primary mb-1">Groups</label>
            <UserGroupsChecklist
              customerId={user.customer_id}
              selectedOrgUnitIds={groupIds}
              onChange={setGroupIds}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ems-text-primary mb-1">Site Access</label>
            <SiteAccessChecklist selectedSiteIds={siteIds} onChange={setSiteIds} />
          </div>

          {/* Portal Access */}
          <div className="border border-ems-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-ems-text-muted" />
                <span className="text-sm font-medium text-ems-text-primary">Portal Access</span>
              </div>
              <button
                type="button"
                onClick={() => setIsPortalUser(!isPortalUser)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isPortalUser ? 'bg-ems-green' : 'bg-ems-border'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${isPortalUser ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <p className="text-xs text-ems-text-muted mt-1.5">
              {isPortalUser ? 'User can access portal.djinems.com' : 'User cannot access the internal portal'}
            </p>
          </div>

          {/* MFA Section */}
          <div className="border border-ems-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-ems-text-muted" />
              <span className="text-sm font-medium text-ems-text-primary">Multi-Factor Auth</span>
            </div>

            {isEditingSelf ? (
              mfaStatus?.enabled ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-ems-green" />
                    <span className="text-sm text-ems-green font-medium">MFA Enabled</span>
                  </div>
                  {mfaStatus.backupCodesRemaining != null && (
                    <p className="text-xs text-ems-text-muted">
                      {mfaStatus.backupCodesRemaining} backup code{mfaStatus.backupCodesRemaining !== 1 ? 's' : ''} remaining
                    </p>
                  )}
                  <button
                    onClick={() => setShowMfaDisable(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ems-red/20 text-xs text-ems-red hover:bg-ems-red/5"
                  >
                    <ShieldOff className="w-3.5 h-3.5" />
                    Disable MFA
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-ems-text-muted">Not enrolled</p>
                  <button
                    onClick={() => setShowMfaSetup(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ems-green/20 text-xs text-ems-green hover:bg-ems-green/5"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Enable MFA
                  </button>
                </div>
              )
            ) : (
              <p className="text-xs text-ems-text-muted">MFA can only be managed by the user themselves</p>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <button
              onClick={handleSendResetEmail}
              className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm text-ems-text-secondary hover:bg-ems-bg-card-hover flex items-center justify-center gap-2"
            >
              <Mail className="w-3.5 h-3.5" />
              Send Password Reset Email
            </button>
            {user.is_active ? (
              <button
                onClick={handleDeactivate}
                className="w-full px-3 py-2 rounded-lg border border-ems-red/20 text-sm text-ems-red hover:bg-ems-red/5"
              >
                Deactivate User
              </button>
            ) : (
              <button
                onClick={handleReactivate}
                className="w-full px-3 py-2 rounded-lg border border-ems-green/20 text-sm text-ems-green hover:bg-ems-green/5"
              >
                Reactivate User
              </button>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-ems-border">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-ems-border text-sm text-ems-text-secondary hover:bg-ems-bg-card-hover">Cancel</button>
            <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !!loadError} className="px-4 py-2 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 text-sm font-medium hover:bg-ems-forest/80 disabled:opacity-50">
              {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* MFA Modals */}
      {showMfaSetup && (
        <MfaSetupModal
          onClose={() => setShowMfaSetup(false)}
          onComplete={handleMfaComplete}
        />
      )}
      {showMfaDisable && (
        <MfaDisableModal
          onClose={() => setShowMfaDisable(false)}
          onComplete={handleMfaComplete}
        />
      )}
    </div>
  );
}
